import { DateProvider } from "@/shared/DateProvider";
import { UrlHelpers } from "@/shared/services/UrlHelpers";
import IndexedDBSingleton, { FileMetadataEntry } from "@/utils/IndexedDB";
import { FileSystemRecord } from "./FileSystemRecord";

export class VirtualFileSystem {

    private static instance: VirtualFileSystem

    public static getInstance(): VirtualFileSystem {
      if (!VirtualFileSystem.instance) {
        VirtualFileSystem.instance = new VirtualFileSystem()
      }
      return VirtualFileSystem.instance
    }

    private constructor() {
    }

    async exist(url: string): Promise<boolean>{
        const record = await IndexedDBSingleton.getItem<FileSystemRecord>(url);
        return record != null;
    }

    async write(url: string, record: FileSystemRecord){
        await this.createFolderIfNone(url)
        await IndexedDBSingleton.setItem<FileSystemRecord>(url, record);
    }

    async getFileExtensionForUrl(url: string): Promise<string | null> {
        let urlWithoutQuery = url.split('?')[0];
        let matchingUrls = await this.list(urlWithoutQuery)
        if (matchingUrls.length == 0){
            return null;
        }
        
        let urlComponents = matchingUrls[0][0].split('.');
        if (urlComponents.length == 1) {
            return null;
        }
        
        let extensionWithQueryString = urlComponents[urlComponents.length - 1];
        let extension = extensionWithQueryString.split('?')[0];
        return extension
    }

    // async writeBlob(url: string, blob: Blob) {
    //     let blobUrl = await this.parseURI(blob);
    //     return await this.writeBlobUrl(url, blobUrl);
    // }

    async writeBlob(path: string, blob: Blob) {
        const now  = DateProvider.getUTCDate();
        const size = blob.size / 1024;
      
        const rec  = new FileSystemRecord(
          blob, now, now, now,
          size,
          this.generateRandom12DigitNumber(),
          false,
          {}
        );
      
        const ext = path.split('.').pop();
        if (ext && !ext.includes('/')) rec.format = ext.split('?')[0];
      
        await this.write(path, rec);
      }

      async writeBlobUrl(path: string, blobUrl: string) {
        // convert the incoming URL to a real Blob and delegate
        const blob = await this.extractBlobFromUrl(blobUrl);
        return this.writeBlob(path, blob);               // ⬅️ new API
      }

    // Method to link an existing file with a new path
    async link(sourcePath: string, destinationPath: string): Promise<boolean> {
        return await this.copyIsHardCopy(sourcePath, destinationPath, false);
    }

    // Method to copy a file from one path to another
    async copy(sourcePath: string, destinationPath: string): Promise<boolean> {
        return await this.copyIsHardCopy(sourcePath, destinationPath, true);
    }

    private async copyIsHardCopy(sourcePath: string, destinationPath: string, isHardCopy: boolean): Promise<boolean> {
        return await IndexedDBSingleton.copyIsHardCopyDirect(sourcePath, destinationPath, isHardCopy);
    }

    async deleteByPrefix(prefix: string, idsToPreserve: string[] = []): Promise<void> {
        return IndexedDBSingleton.deleteByPrefix(prefix, idsToPreserve);
    }

    // Method to move a file or folder from one path to another
    async move(sourcePath: string, destinationPath: string): Promise<boolean> {
        const record = await IndexedDBSingleton.getItem<FileSystemRecord>(sourcePath);
        if (!record) {
            return false; // The source path doesn't exist
        }
    
        // Ensure the source is a folder if we're moving recursively
        if (!record.isFolder) {
            return this.moveSingleFile(sourcePath, destinationPath);
        }
    
    
        // Move all subfiles and subfolders
        const sourcePathList = await this.list(sourcePath);
        for (const [key, subRecord] of sourcePathList) {
            const relativePath = key.substring(sourcePath.length);
            const newDestinationPath = destinationPath + relativePath;
            await this.write(newDestinationPath, subRecord);
            await IndexedDBSingleton.deleteItem(key);
        }
    
        // Remove the original folder
        await IndexedDBSingleton.deleteItem(sourcePath);
        return true; // Successfully moved
    }
    
    // Helper method to move a single file
    private async moveSingleFile(sourcePath: string, destinationPath: string): Promise<boolean> {
        const record = await IndexedDBSingleton.getItem<FileSystemRecord>(sourcePath);
        if (!record) {
            return false; // The source file doesn't exist
        }
    
        let filename = sourcePath.split('/').pop();
        let destinationFile = destinationPath + '/' + filename;
    
        const destinationExists = await IndexedDBSingleton.getItem(destinationFile);
        if (destinationExists) {
            return false; // The destination already exists
        }
    
        await this.write(destinationFile, record);
        await IndexedDBSingleton.deleteItem(sourcePath);
        return true; // Successfully moved
    }
    
    
    async get(path: string): Promise<FileSystemRecord | null> {
        const raw = await IndexedDBSingleton.getItem<FileSystemRecord>(path);
        return raw ? Object.setPrototypeOf(raw, FileSystemRecord.prototype) : null;
    }

    async restoreUndoDate(path: string, date: Date): Promise<FileSystemRecord | null> {
        const record = await this.get(path);
        if (!record) return null;
      
        record.undoDate = date;                    // mutate in place
        await IndexedDBSingleton.setItem<FileSystemRecord>(path, record);
        return record;                             // already prototype-hydrated
      }

    async deleteAll(paths: string[], includeMemory:boolean = true): Promise<boolean> {
        for (let path of paths) {
            await this.delete(path, includeMemory);
        }
        return true;
    }
    
    async delete(path: string, includeMemory:boolean = true): Promise<boolean> {
        let allSubRecords = await this.list(path);
        let keys = allSubRecords.map(t=>t[0])
        await IndexedDBSingleton.deleteItems(keys, includeMemory)
        return true;
    }

    // Method to create a folder if it doesn't exist
    async createFolderIfNone(filePath: string): Promise<boolean>  {
        let fileComponents = filePath.split('.');
        let folderPath = fileComponents[0];
        if (!folderPath.startsWith('/')) {
            folderPath = '/' + folderPath;
        }
    
        let parts = folderPath.split('/');
        if (fileComponents.length > 1) {
            parts.pop();
        }

        let currentPath = '';
        for (let i = 0; i < parts.length; i++) {
            if (parts[i] === '') continue; // Skip empty parts due to leading slash
    
            currentPath = currentPath ? currentPath + '/' + parts[i] : parts[i];
    
            // Ensure current path starts with a '/'
            if (!currentPath.startsWith('/')) {
                currentPath = '/' + currentPath;
            }
    
            // Check if the current path already exists
            const currentPathExisting = await IndexedDBSingleton.getItem<FileSystemRecord>(currentPath);
            if (currentPathExisting) {
                if (!currentPathExisting.isFolder) {
                    return false; // If the existing path is not a folder, cannot proceed
                }
            } else {
                // Create a new folder record for the current path
                const folderRecord = new FileSystemRecord(
                    null,
                    DateProvider.getUTCDate(),
                    DateProvider.getUTCDate(),
                    DateProvider.getUTCDate(),
                    0, // Size for a folder is 0
                    this.generateRandom12DigitNumber(),
                    true, // It is a folder
                    {}
                );
                await IndexedDBSingleton.setItem<FileSystemRecord>(currentPath, folderRecord);
            }
        }
        return true; // Successfully created all necessary folders
    }

      // Method to list all subfiles available under a given URL/path
      async list(path: string): Promise<[string, FileSystemRecord][]> {
        const listed = await IndexedDBSingleton.list(path);
        return listed
      }

      // Metadata-only listing of files under a path. Never reads blob bytes (see
      // IndexedDBSingleton.listMetadata), so it is safe for hot metadata-only callers
      // (e.g. building the sync file index) regardless of the active storage strategy.
      async listMetadata(path: string): Promise<FileMetadataEntry[]> {
        return IndexedDBSingleton.listMetadata(path);
      }

    async resetDate(filePath: string, newDate: Date): Promise<boolean> {
        const record = await IndexedDBSingleton.getItem<FileSystemRecord>(filePath);
        if (!record) {
            return false;
        }
        
        record.createdDate = newDate;
        record.modificationDate = newDate;

        await IndexedDBSingleton.setItem<FileSystemRecord>(filePath, record);
        return true;
    }

    async extractSizeFromBlobUrl(blobUrl: string): Promise<number> {
        const blob = await this.extractBlobFromUrl(blobUrl);
        return this.getBlobSizeInKB(blob);
    }
      

    private async extractBlobFromUrl(url: string): Promise<Blob> {
    try {
        const res = await fetch(url);
        if (!res.ok) {
            throw new Error(`Failed to fetch blob: ${res.statusText}`);
        }
        return res.blob();
    } catch (error) {
        console.error('Error extracting blob from URL:', error);
        throw error;
    }
}

    private getBlobSizeInKB(blob: Blob): number {
        const sizeInBytes = blob.size; // Size in bytes
        const sizeInKB = sizeInBytes / 1024; // Convert to kilobytes
        return sizeInKB;
    }

    // Function to copy a Blob
    private async copyBlobUrl(originalBlobUrl: string): Promise<string> {
        let originalBlob = await this.blobUrlToBlob(originalBlobUrl);
        // Use Blob's arrayBuffer method to read its data
        const arrayBuffer = await originalBlob.arrayBuffer();
        
        // Create a new Blob with the same data
        const copiedBlob = new Blob([arrayBuffer], { type: originalBlob.type });
        
        let copiedBlobUrl = this.parseURI(copiedBlob);
        return copiedBlobUrl;
    }

    private async parseURI(blob: Blob): Promise<string> {
        return new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result as string);
          r.onerror = rej;
          r.readAsDataURL(blob);
        });
    }

    // Function to convert a Blob URL back to a Blob object
    async blobUrlToBlob(blobUrl: string): Promise<Blob> {
        return this.extractBlobFromUrl(blobUrl);
    }

    generateRandom12DigitNumber(): number {
        let randomNumber = '';
        for (let i = 0; i < 12; i++) {
            const digit = Math.floor(Math.random() * 10); // Generate a random digit from 0 to 9
            randomNumber += digit.toString();
        }
        return parseInt(randomNumber);
    }

    /**
     * Revokes all blob URLs and clears base64 caches for records under the given prefix.
     * Does NOT delete the records from IndexedDB - only cleans up in-memory caches.
     * Call this when switching projects or leaving the editor to free memory.
     */
    async revokeAllBlobUrls(prefix: string): Promise<void> {
        const records = await this.list(prefix);
        for (const [_path, record] of records) {
            record.cleanMemory();
        }
    }
}


